import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { fetchData } from '../../Utils/Server';
import Endpoints from '../../Constants/Endpoints';
import PageLayout from '../Layout/PageLayout';
import { Card, CardTitleBar, StatCard, FormField, TextInput, Button, Spinner } from '../UI';
import { tokens } from '../../theme/tokens';

const fmtKES = (cents) => `KES ${((cents || 0) / 100).toFixed(2)}`;

const ymd = (d) => d.toISOString().slice(0, 10);

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${tokens.spacing[4]};
  margin-bottom: ${tokens.spacing[5]};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: ${tokens.spacing[4]};
  margin-bottom: ${tokens.spacing[5]};
`;

const KVTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${tokens.typography.fonts.body};
  font-size: ${tokens.typography.size.base};

  th, td {
    padding: ${tokens.spacing[3]} ${tokens.spacing[4]};
    border-bottom: 1px solid ${tokens.colors.divider};
    color: ${tokens.colors.body};
  }
  th {
    font-size: ${tokens.typography.size.xs};
    font-weight: ${tokens.typography.weight.bold};
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${tokens.colors.muted};
    text-align: left;
  }
  td.num, th.num { text-align: right; }
  tr:last-child td { border-bottom: 0; }
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${tokens.spacing[5]};
`;

export default function Dashboard() {
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const [from, setFrom] = useState(ymd(monthAgo));
  const [to, setTo] = useState(ymd(today));
  const [kpi, setKpi] = useState(null);
  const [tiers, setTiers] = useState({});
  const [categories, setCategories] = useState({});

  const load = useCallback(() => {
    const qs = `?from=${from}&to=${to}`;
    fetchData(Endpoints.ANALYTICS_OVERALL + qs).then((r) => r.json()).then((res) => setKpi((res && res.data) || res));
    fetchData(Endpoints.ANALYTICS_REVENUE_BY_TIER + qs).then((r) => r.json()).then((res) => setTiers((res && res.data) || res || {}));
    fetchData(Endpoints.ANALYTICS_COMPLAINTS_BY_CATEGORY + qs).then((r) => r.json()).then((res) => setCategories((res && res.data) || res || {}));
  }, [from, to]);

  useEffect(load, [load]);

  return (
    <PageLayout title="Owner dashboard">
      <Filters>
        <FormField label="From" htmlFor="analytics-from" flush>
          <TextInput id="analytics-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </FormField>
        <FormField label="To" htmlFor="analytics-to" flush>
          <TextInput id="analytics-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </FormField>
        <a
          href={`${Endpoints.SERVER_HOST}/api/v1/analytics/overall?from=${from}&to=${to}&format=csv`}
          target="_blank" rel="noreferrer"
          style={{ textDecoration: 'none' }}>
          <Button variant="outline">Download CSV</Button>
        </a>
      </Filters>

      {!kpi ? <Spinner center label="Loading…" /> : (
        <>
          <Grid>
            <StatCard label="Revenue" value={fmtKES(kpi.revenue_cents)} hint={`ARPU ${fmtKES(kpi.arpu_cents)}`} accent />
            <StatCard label="Completed" value={kpi.completed_requests || 0} hint={`${(kpi.otd_percent || 0).toFixed(1)}% on-time`} accent={false} />
            <StatCard label="Cancelled" value={kpi.cancelled_requests || 0} accent={false} />
            <StatCard label="Unique users" value={kpi.unique_users || 0} accent={false} />
            <StatCard label="Active riders" value={kpi.active_riders || 0} accent={false} />
            <StatCard label="Complaints" value={kpi.complaint_count || 0} accent={false} />
          </Grid>

          <Cards>
            <Card padded={false}>
              <CardTitleBar title="Revenue by tier" />
              <KVTable>
                <thead><tr><th>Tier</th><th className="num">KES</th></tr></thead>
                <tbody>
                  {Object.entries(tiers).map(([tier, amount]) => (
                    <tr key={tier}><td>{tier}</td><td className="num">{(amount || 0).toFixed(2)}</td></tr>
                  ))}
                </tbody>
              </KVTable>
            </Card>

            <Card padded={false}>
              <CardTitleBar title="Complaints by category" />
              <KVTable>
                <thead><tr><th>Category</th><th className="num">Count</th></tr></thead>
                <tbody>
                  {Object.entries(categories).map(([cat, count]) => (
                    <tr key={cat}><td>{cat}</td><td className="num">{count}</td></tr>
                  ))}
                </tbody>
              </KVTable>
            </Card>
          </Cards>
        </>
      )}
    </PageLayout>
  );
}
